import React, { useState } from "react";
import { Card, CardBody, Button, Table, Badge, Spinner } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import CreateRuleModal from "../../components/AutomationRules/CreateRuleModal";
import RuleLogsModal from "../../components/AutomationRules/RuleLogsModal";
import useAutomationRules from "../../Hooks/useAutomationRules";
import { toast } from "react-toastify";

const Index = () => {
  const [modal, setModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logsRule, setLogsRule] = useState(null);

  const { rules, loading, createRule, updateRule, toggleRule, resumeUser, fetchRules } = useAutomationRules();

  const handleEdit = (rule) => {
    setSelectedRule(rule);
    setModal(true);
  };

  const handleOpenLogs = (rule) => {
    setLogsRule(rule);
    setLogsModalOpen(true);
  };

  const handleResumeUser = async (ruleId, userId, userName) => {
    try {
      await resumeUser(ruleId, { userId });
      toast.success(`${userName} unpaused successfully! Status is now active (🟢 Green).`);
    } catch (err) {
      toast.error(err?.message || "Failed to resume user");
    }
  };

  return (
    <div className="page-content">
      <Breadcrumbs title="Leads" breadcrumbItem="Assignment Rules" />
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0"> Automation Rules </h5>
            <Button color="primary" onClick={() => setModal(true)}><i className="mdi mdi-plus me-1" /> Create Rule</Button>
          </div>

          <div className="table-responsive">
            <Table className="align-middle table-hover">
              <thead className="table-light">
                <tr>
                  <th>Rule Name</th>
                  <th>Projects</th>
                  <th>Rule Members</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="text-center py-4"><Spinner size="sm" color="primary" /></td></tr>
                ) : rules.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-4 text-muted">No Rules Found</td></tr>
                ) : (
                  rules.map((r) => {
                    const pausedMap = new Map();
                    (r.pausedUsers || []).forEach((p) => {
                      pausedMap.set(String(p.userId?._id || p.userId), p);
                    });

                    return (
                      <tr key={r._id}>
                        <td className="fw-bold">{r.ruleName}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {(r.projectIds || []).map((p) => (
                              <Badge key={p._id} color="info" pill>{p.projectName}</Badge>
                            ))}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {(r.includeUsers || []).map((u) => {
                              const isPaused = pausedMap.has(String(u._id));
                              return (
                                <Badge
                                  key={u._id}
                                  color={isPaused ? "warning" : "light"}
                                  className={isPaused ? "text-dark border border-warning shadow-sm" : "text-dark border"}
                                  style={{ cursor: isPaused ? "pointer" : "default" }}
                                  title={isPaused ? "Click to Resume (Unpause) user" : "Active Rule Member"}
                                  onClick={() => isPaused && handleResumeUser(r._id, u._id, `${u.firstName} ${u.lastName}`)}
                                >
                                  {isPaused ? "🟡 " : "🟢 "}{u.firstName} {u.lastName}
                                </Badge>
                              );
                            })}
                          </div>
                        </td>
                        <td>
                          <Badge color={r.isActive ? "success" : "danger"}>{r.isActive ? "Active" : "Inactive"}</Badge>
                        </td>
                        <td className="d-flex gap-2">
                          <Button size="sm" color="primary" title="Edit Rule" onClick={() => handleEdit(r)}><i className="mdi mdi-pencil" /></Button>
                          <Button size="sm" color="info" title="View Rule Activity Logs" onClick={() => handleOpenLogs(r)}><i className="mdi mdi-history" /></Button>
                          <Button size="sm" color="light" title="Toggle Rule Active Status" onClick={() => toggleRule(r._id)}><i className="mdi mdi-sync" /></Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      <CreateRuleModal
        isOpen={modal}
        toggle={() => { setModal(false); setSelectedRule(null); fetchRules(); }}
        editData={selectedRule}
        onSubmit={async (data) => {
          if (selectedRule) {
            await updateRule(selectedRule._id, data);
          } else {
            await createRule(data);
          }
          fetchRules();
        }}
        onRuleUpdated={fetchRules}
      />
      <RuleLogsModal isOpen={logsModalOpen} toggle={() => setLogsModalOpen(false)} rule={logsRule} />
    </div>
  );
};

export default Index;
